import React, { useState, useEffect, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Check } from 'lucide-react';

// Whale/ocean SVG icon — simplified for small pill size, clean minimal line-drawing
const WhaleIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    {/* Simplified whale silhouette — body, tail, eye, spout */}
    <path d="M2 15c1.5-3 4-5 8-5 3 0 5.5 1 7 3 .8 1 1.5 2.5 1 4-.5 1.5-2 2-4 2H6c-2.5 0-4-1.5-4-4z" />
    <path d="M21 10c.5-2 1-3 1.5-2" />
    <circle cx="7" cy="14" r=".7" fill="currentColor" stroke="none" />
    <path d="M17.5 16.5c-1 .3-2 0-2.5-.5" />
  </svg>
);

const HELP_CONTENT = {
  '/': {
    feature: 'Dashboard',
    subtitle: 'Mission Control',
    what: 'Your BD intelligence at a glance — pipeline value, recent research, and what needs attention today.',
    steps: [
      'Check Needs Attention first — anyone showing here needs a touchpoint today. Click Re-engage to generate a warm message instantly.',
      'Hit Generate Summary every Monday morning for your weekly priorities and top 3 recommended actions.',
      'Click Scan for Grants to find new funding opportunities relevant to your projects.',
      'Use the four shortcut cards at the bottom to jump to the most common actions.',
    ],
    tips: [
      'Pipeline Value and Weighted Value show £0 until you add deal values to your pipeline cards.',
      'The Recent Intelligence panel is a quick route back to anything you just researched.',
    ],
  },
  '/research': {
    feature: 'Research Engine',
    subtitle: 'Surface Intelligence',
    what: 'Get a full AI-powered intelligence report on any target in under 90 seconds.',
    steps: [
      'Click the mode button that matches what you\'re researching — Research an organisation, Profile an individual, or Paste a URL.',
      'Type or paste your query.',
      'Hit the teal Research button and wait 30–90 seconds.',
      'Review the report then click Save to Targets.',
    ],
    tips: [
      'Paste a LinkedIn URL for richer individual profiles.',
      'Switch to Category Discovery to find multiple targets at once — grants, events, press contacts.',
    ],
  },
  '/pipeline': {
    feature: 'Pipeline',
    subtitle: 'Relationship Tracking',
    what: 'Track every live opportunity — drag cards between stages as relationships progress toward deals.',
    steps: [
      'Drag any card to a new column as the relationship moves forward.',
      'Click any card to open the slide-out detail panel — log contacts, add notes, see full relationship history.',
      'Add deal values in the slide-out panel to make Dashboard metrics meaningful.',
      'Use Generate Meeting Brief from any card before an important call.',
    ],
    tips: [
      'Cards with gold due dates are overdue — address those first.',
      'Log Contact after every touchpoint to keep records current.',
    ],
  },
  '/outreach': {
    feature: 'Outreach Generator',
    subtitle: '',
    what: 'Generate tailored outreach messages written in your voice, pre-loaded with target intelligence.',
    steps: [
      'Select a target from your database.',
      'Select the relevant project.',
      'Click the outreach type — Cold Intro, Warm Follow-up, Meeting Request, Post-meeting Follow-up, or Re-engagement.',
      'Hit Generate Draft, personalise, and copy to send from your email client.',
    ],
    tips: [
      'Always personalise before sending.',
      'Run a Context Import of your last conversation first for warm contacts.',
    ],
  },
  '/context-import': {
    feature: 'Import Context',
    subtitle: 'Paste and Extract',
    what: 'Paste any email, WhatsApp, meeting notes, or call summary — BREACH extracts contacts, updates pipeline, and surfaces next actions automatically.',
    steps: [
      'Paste your content into the text area — any format, stream of consciousness is fine.',
      'Click Extract Data.',
      'Review the structured preview — each item has a checkbox.',
      'Deselect anything you don\'t want applied, then click Apply Updates.',
    ],
    tips: [
      'Include full email signatures — addresses extract automatically.',
      'Use the target-specific Import Context on individual target cards for more precise extraction.',
    ],
  },
  '/targets': {
    feature: 'Target Intelligence',
    subtitle: '',
    what: 'Your full intelligence database — every organisation and individual you have researched, permanently stored.',
    steps: [
      'Use filter chips to browse by type.',
      'Use the sort dropdown to find most recent or highest relevance targets.',
      'Click any card to open the full intelligence profile.',
      'Use the pencil icon on any section to add your own knowledge on top of the AI research.',
      'Scroll to Import Context at the bottom of any target card to add new intelligence after a call.',
    ],
    tips: [
      'Find Duplicates keeps your database clean.',
      'The Owner filter lets you see each owner\'s targets separately.',
    ],
  },
  '/projects': {
    feature: 'Projects',
    subtitle: 'Projects',
    what: 'Manage your active projects and products — all pipeline activity maps back here.',
    steps: [
      'Click any project to see its detail on the right.',
      'Click Research Targets for This Project to find new targets for this project.',
      'Click View Pipeline to see all active opportunities for this project.',
      'Click + Add Project to create a new project.',
    ],
    tips: [
      'Add Key Events to each project — conferences, key dates, grant deadlines — so important dates are always visible.',
    ],
  },
  '/calendar': {
    feature: 'Market Events',
    subtitle: '',
    what: 'Track relevant conferences and markets — with automatic discovery and pre-event outreach sequences.',
    steps: [
      'Use filter chips to browse by event type.',
      'Click Scan for New Events to automatically discover new conferences.',
      'Click Show Targets to see which pipeline contacts are relevant to an event.',
      'Click Pre-event Outreach to generate a tailored outreach sequence for all relevant targets.',
    ],
    tips: [
      'Use Pre-event Outreach 3–4 weeks before any conference.',
      'The AI DISCOVERED badge shows which events were found automatically.',
    ],
  },
};

const VISIT_KEY = 'breach_help_visits';

const getVisitCount = (path) => {
  try {
    const visits = JSON.parse(localStorage.getItem(VISIT_KEY) || '{}');
    return visits[path] || 0;
  } catch { return 0; }
};

const incrementVisit = (path) => {
  try {
    const visits = JSON.parse(localStorage.getItem(VISIT_KEY) || '{}');
    visits[path] = (visits[path] || 0) + 1;
    localStorage.setItem(VISIT_KEY, JSON.stringify(visits));
    return visits[path];
  } catch { return 0; }
};

// Context for sharing open state between button and panel
const HelpContext = createContext({ open: false, setOpen: () => {}, content: null });

export const useHelp = () => useContext(HelpContext);

// Provider wraps in Layout
export const HelpProvider = ({ children }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const basePath = '/' + (location.pathname.split('/')[1] || '');
  const content = HELP_CONTENT[basePath];
  return (
    <HelpContext.Provider value={{ open, setOpen, content, basePath }}>
      {children}
    </HelpContext.Provider>
  );
};

// Inline button to place next to H1 titles
export const HelpButton = ({ className = '' }) => {
  const location = useLocation();
  const { setOpen, content } = useHelp();
  const [shouldPulse, setShouldPulse] = useState(false);
  const basePath = '/' + (location.pathname.split('/')[1] || '');

  useEffect(() => {
    const count = incrementVisit(basePath);
    setShouldPulse(count <= 3);
    const timeout = setTimeout(() => setShouldPulse(false), 30000);
    return () => clearTimeout(timeout);
  }, [basePath]);

  if (!content) return null;

  return (
    <button
      onClick={() => setOpen(true)}
      className={`inline-flex items-center px-3 py-1 rounded-full bg-transparent text-[#00D4C8] border border-[#00D4C8]/30 text-xs font-medium transition-all hover:border-[#00D4C8]/60 hover:bg-[#00D4C8]/5 shrink-0 ${shouldPulse ? 'help-pulse' : ''} ${className}`}
      data-testid="help-pill-button"
    >
      How does this work?
    </button>
  );
};

// Slide-out panel — rendered once in Layout
export const HelpSlidePanel = () => {
  const { open, setOpen, content } = useHelp();

  if (!open || !content) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="help-panel-overlay">
      <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
      <div
        className="absolute right-0 top-0 h-full w-full max-w-md bg-[#0E1C33] border-l border-[#1A2F4C] shadow-2xl flex flex-col animate-slide-in-right"
        data-testid="help-panel"
      >
        {/* Header */}
        <div className="p-6 border-b border-[#1A2F4C] flex items-start justify-between">
          <div>
            <h2 className="font-heading text-2xl text-[#00D4C8]">{content.feature}</h2>
            {content.subtitle && <p className="text-sm text-[#A3B5C7] mt-1">{content.subtitle}</p>}
          </div>
          <button
            onClick={() => setOpen(false)}
            className="p-2 rounded-lg hover:bg-[#1A2F4C] text-[#A3B5C7] hover:text-white transition-colors"
            data-testid="help-panel-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div>
            <h3 className="text-xs uppercase tracking-[0.15em] text-[#A3B5C7] mb-2 font-medium">What it does</h3>
            <p className="text-[#D4E2F0] leading-relaxed">{content.what}</p>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.15em] text-[#A3B5C7] mb-3 font-medium">Step by step</h3>
            <div className="space-y-3">
              {content.steps.map((step, i) => (
                <div key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#00D4C8]/10 text-[#00D4C8] text-xs flex items-center justify-center font-medium shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-sm text-[#D4E2F0] leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.15em] text-[#00D4C8] mb-3 font-medium">Pro tips</h3>
            <div className="space-y-2">
              {content.tips.map((tip, i) => (
                <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-[#00D4C8]/5 border border-[#00D4C8]/10">
                  <span className="text-[#00D4C8] text-xs mt-0.5 shrink-0">*</span>
                  <p className="text-sm text-[#D4E2F0] leading-relaxed">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#1A2F4C]">
          <button
            onClick={() => setOpen(false)}
            className="w-full py-3 rounded-lg bg-[#00D4C8] text-[#0A1628] font-medium text-sm hover:bg-[#33DDD3] transition-colors flex items-center justify-center gap-2"
            data-testid="help-panel-got-it"
          >
            <Check className="w-4 h-4" strokeWidth={2} />
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

// Default export for backward compatibility
const HelpPanel = HelpSlidePanel;
export default HelpPanel;
